import React, { useState, useEffect } from 'react';
import Card from '@/components/common/Card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Clock, MapPin, Calendar, LogIn, LogOut } from 'lucide-react';
import { useAppSelector } from '@/store/store';
import { AttendanceService } from '@/services/Attendance/AttendanceService';
import { showToastNotification } from '@/utility/helper';
import { ThrowError } from '@/api/callApi';
import moment from 'moment';

interface IAttendanceState {
  attendanceId: string | null;
  checkInTime: string | null;
  checkOutTime: string | null;
}

const TodayAttendance: React.FC = () => {
  const user = useAppSelector((state) => state.user.user) as any;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [attendance, setAttendance] = useState<IAttendanceState>({
    attendanceId: null,
    checkInTime: null,
    checkOutTime: null,
  });

  // Live clock interval
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Helper to format date in YYYY-MM-DD format
  const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch today's attendance record
  const fetchTodayAttendance = async () => {
    if (!user?.id) return;
    try {
      const todayStr = getLocalDateString();
      // const params = `todayAttendance=true&myAttendance=true`;
      const response = await AttendanceService.getAttendance();

      if (response?.status && response?.data?.attendance) {
        // Find today's record matching current user id and today's date
        const record = response.data.attendance.find(
          (att: any) => att.userId === user.id && att.date && att.date.startsWith(todayStr)
        );

        if (record) {
          setAttendance({
            attendanceId: record.id,
            checkInTime: record.checkInTime,
            checkOutTime: record.checkOutTime,
          });
        } else {
          setAttendance({ attendanceId: null, checkInTime: null, checkOutTime: null });
        }
      }
    } catch (error) {
      console.error("Error fetching today's attendance:", error);
    }
  };

  useEffect(() => {
    fetchTodayAttendance();
  }, [user?.id]);

  // Handle Check-In / Check-Out
  const handleCheckInOut = async (actionType: 'checkIn' | 'checkOut') => {
    if (!user?.id) return;
    // console.log("user?.shift?.id", user?.shift?.id)
    setIsLoading(true);
    try {
      const todayStr = getLocalDateString();
      const currentTimeStr = moment().format('HH:mm:ss');

      const payload: any = {
        // userId: user.id,
        date: todayStr,
        status: "PRESENT" as const,
        remarks: actionType === 'checkIn' ? "Checked in via web dashboard" : "Checked out via web dashboard",
        markingAttendance: true,
        isMarkedByHRM: false,
        shiftId: user?.shift?.id,
      };
      // console.log("payload", payload)
      if (actionType === 'checkIn') {
        payload.checkInTime = currentTimeStr;
      } else {
        payload.checkOutTime = currentTimeStr;
        payload.attendanceId = attendance.attendanceId;
      }

      const response = await AttendanceService.markAttendance(payload);
      if (response?.status) {
        showToastNotification('success', response.message || `Successfully ${actionType === 'checkIn' ? 'Checked In' : 'Checked Out'}`);
        await fetchTodayAttendance();
      } else {
        showToastNotification('danger', response.message || 'Action failed');
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to format time into 12h AM/PM
  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '--:--';
    // If it's full ISO datetime string
    if (timeStr.includes('T') || timeStr.includes('-')) {
      return moment.utc(timeStr).format('hh:mm A');
    }
    // If it's plain time string
    return moment(timeStr, 'HH:mm:ss').format('hh:mm A');
  };

  // Calculate total hours
  const calculateTotalHours = (checkIn: string | null, checkOut: string | null) => {
    if (!checkIn) return '--:--';

    const getMomentObj = (timeStr: string) => {
      if (timeStr.includes('T') || timeStr.includes('-')) {
        return moment.utc(timeStr);
      }
      return moment(timeStr, 'HH:mm:ss');
    };

    const start = getMomentObj(checkIn);
    const end = checkOut ? getMomentObj(checkOut) : moment();

    const duration = moment.duration(end.diff(start));
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.minutes());

    if (hours < 0 || minutes < 0 || isNaN(hours) || isNaN(minutes)) return '00:00';

    const pad = (num: number) => String(num).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}`;
  };

  const isPunchAllowed = user?.site?.isPunchAllowed ?? true;

  return (
    <Card className="relative overflow-hidden border border-slate-200/60 shadow-sm p-6 bg-linear-to-b from-white to-slate-50/50">
      <div className="absolute -top-10 -right-10 p-4 opacity-10">
        <Clock className='w-24 h-24 lg:w-32 lg:h-32 text-primary' />
      </div>
      <div className="relative z-10 flex flex-col h-full flex-1">
        <div className="mb-6">
          <h2 className="text-4xl font-bold tracking-tight mb-2 text-slate-800">
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </h2>
          <p className="text-slate-500 text-sm flex items-center gap-1.5 font-medium">
            <Calendar className="w-4 h-4 text-primary/70" />
            {currentTime.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <Badge variant='outline' className="w-fit border-slate-200 text-slate-700 bg-slate-50/50 mb-5">
          <MapPin className="w-3.5 h-3.5 mr-1.5 text-primary" />
          {user?.site?.name || 'OFFICE LOCATION'}
        </Badge>

        {isPunchAllowed ? (
          <div className="mt-2">
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Button
                className='w-full bg-primary hover:bg-primary/95 text-white shadow-xs font-semibold'
                disabled={isLoading || !!attendance.checkInTime}
                onClick={() => handleCheckInOut('checkIn')}
              >
                {/* {isLoading ? ( */}
                {/* <Spinner className="w-4 h-4 mr-2" /> */}
                {/* ) : ( */}
                <LogIn className="w-4 h-4 mr-2" />
                {/* )} */}
                {attendance.checkInTime ? 'Checked In' : 'Check In'}
              </Button>
              <Button
                className='w-full font-semibold border-slate-200 text-slate-700 hover:bg-slate-50'
                variant='outline'
                // disabled={isLoading || !attendance.checkInTime || !!attendance.checkOutTime}
                onClick={() => handleCheckInOut('checkOut')}
              >
                {isLoading && attendance.checkInTime && !attendance.checkOutTime ? (
                  <Spinner className="w-4 h-4 mr-2" />
                ) : (
                  <LogOut className="w-4 h-4 mr-2" />
                )}
                {attendance.checkOutTime ? 'Checked Out' : 'Check Out'}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center rounded-xl p-3 border border-slate-200/60 bg-white shadow-xs">
              <div>
                <p className="text-sm font-semibold text-slate-700">{formatTime(attendance.checkInTime)}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5 font-medium">Check In</p>
              </div>
              <div className="border-x border-slate-100">
                <p className="text-sm font-semibold text-slate-700">{formatTime(attendance.checkOutTime)}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5 font-medium">Check Out</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-600">
                  {calculateTotalHours(attendance.checkInTime, attendance.checkOutTime)}
                </p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5 font-medium">Total Hrs</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center p-4 border border-dashed border-slate-200 rounded-xl bg-slate-50/50 mt-2">
            <p className="text-xs text-slate-500 font-medium">Punching not allowed at your location</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TodayAttendance;
